import { useCallback, useEffect, useRef, useState } from "react";
import { isApiError } from "../api/client";
import { postOutboundIntent } from "../api/endpoints";

/**
 * The only way traffic reaches an advertiser (docs/06 section 6): a real anchor whose href is a
 * same-origin `/out/<token>` prepared by `POST /outbound-intents` BEFORE the click, refreshed
 * before it expires, and re-prepared after use (tokens are single-use for eligibility).
 * `target="_blank"` + `rel="noopener noreferrer sponsored"`; no automatic opening, ever.
 */
interface IntentState {
  status: "idle" | "preparing" | "ready" | "unavailable" | "error";
  url: string | null;
  expiresAt: number;
  message: string | null;
}

const REFRESH_MARGIN_MS = 20_000;

export function VisitWebsiteLink({
  propertyId,
  host,
  eager = false,
  className = "btn btn-primary",
  label = "Visit website",
}: {
  propertyId: string;
  host: string;
  /** Prepare on mount (property panel) instead of on hover/focus (long lists). */
  eager?: boolean;
  className?: string;
  label?: string;
}) {
  const [state, setState] = useState<IntentState>({ status: "idle", url: null, expiresAt: 0, message: null });
  const inflight = useRef<Promise<string | null> | null>(null);
  const mounted = useRef(true);

  const prepare = useCallback((): Promise<string | null> => {
    if (inflight.current) return inflight.current;
    setState((s) => (s.status === "ready" ? s : { ...s, status: "preparing" }));
    const p = postOutboundIntent(propertyId)
      .then((res) => {
        if (!mounted.current) return res.url;
        setState({ status: "ready", url: res.url, expiresAt: new Date(res.expiresAt).getTime(), message: null });
        return res.url;
      })
      .catch((err: unknown) => {
        if (!mounted.current) return null;
        if (isApiError(err) && (err.code === "OUTBOUND_INTENT_INVALID" || err.code === "NOT_FOUND")) {
          setState({ status: "unavailable", url: null, expiresAt: 0, message: "No approved website for this listing." });
        } else {
          setState({ status: "error", url: null, expiresAt: 0, message: "Couldn't prepare the link. Try again." });
        }
        return null;
      })
      .finally(() => {
        inflight.current = null;
      });
    inflight.current = p;
    return p;
  }, [propertyId]);

  useEffect(() => {
    mounted.current = true;
    setState({ status: "idle", url: null, expiresAt: 0, message: null });
    if (eager) void prepare();
    return () => {
      mounted.current = false;
    };
  }, [propertyId, eager, prepare]);

  // Refresh before expiry while the link is on screen.
  useEffect(() => {
    if (state.status !== "ready") return;
    const delay = Math.max(1000, state.expiresAt - Date.now() - REFRESH_MARGIN_MS);
    const t = setTimeout(() => void prepare(), delay);
    return () => clearTimeout(t);
  }, [state.status, state.expiresAt, prepare]);

  const warm = () => {
    if (state.status === "idle" || state.status === "error") void prepare();
  };

  if (state.status === "unavailable") {
    return (
      <span className="muted small" role="note">
        {state.message}
      </span>
    );
  }

  if (state.status === "ready" && state.url) {
    return (
      <a
        className={className}
        href={state.url}
        target="_blank"
        rel="noopener noreferrer sponsored"
        title={`Opens ${host} in a new tab`}
        onClick={() => {
          // The token is consumed by this click; prepare a fresh one for the next intentional click.
          setTimeout(() => {
            if (mounted.current) {
              setState((s) => ({ ...s, status: "idle", url: null }));
              void prepare();
            }
          }, 300);
        }}
      >
        {label} ↗
      </a>
    );
  }

  return (
    <button
      type="button"
      className={className}
      onPointerEnter={warm}
      onFocus={warm}
      onTouchStart={warm}
      aria-busy={state.status === "preparing"}
      onClick={async () => {
        // Clicked before the intent was ready: prepare now, then open within the user activation.
        const url = await prepare();
        if (url) window.open(url, "_blank", "noopener,noreferrer");
      }}
    >
      {state.status === "preparing" ? "Preparing link…" : state.status === "error" ? "Retry link" : `${label} ↗`}
    </button>
  );
}
